import * as Sentry from '@sentry/browser'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import { find, groupBy, has, some } from 'lodash-es'
import {
  getPendingPlanInvitationsForBoard,
  getPendingPlanInvitationsForEmail,
  removePlanInvitationForOutsiders
} from '@/services/plan-invitations-api.service.js'
import {
  acceptInvitation,
  getPendingInvitationsForBoard,
  getPendingInvitationsForEmail
} from '@/services/invitations-api.service.js'
import { getPeopleByEmail } from '@/services/people-api.service.js'
import { sendPlanInviteToOutsiders } from '@/services/invitations.service.js'
import { claimUserGroupInvitations } from '@/services/user-groups-api.service.js'
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config'
import { logout, restoreMfaVerifiedClaim, validateAuthState } from '@/services/account-api.service'
import { AUTH_URL, getAuthHeader, setAPIUrl } from '@/services/api.service'
import { authBus, AuthActions } from '@/services/eventbus.service'
import axios from 'axios'
import { logger } from '@/utils/logger'
import { getEmbedStatus } from '@/hooks/use-embed-mode'
import { getDayforceNamespace } from '@/services/dayforce-board-resolution.service'

const PYLON_ALLOWED_NAMESPACES = ['cvshealthstage', 'cvshealth', 'cvshealthconfig']

function loadPylonScript() {
  if (document.querySelector('script[src="/scripts/pylon.js"]')) return
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.src = '/scripts/pylon.js'
  document.head.appendChild(script)
}

/**
 * Authentication state constants
 * Used to determine the current auth state of a user
 */
export const AUTH_STATE = {
  UNAUTHENTICATED: 'unauthenticated',
  UNVERIFIED: 'unverified', // authenticated but email not verified
  VERIFIED: 'verified', // authenticated with verified email
  NEEDS_MFA: 'needs_mfa' // authenticated with verified email but MFA is required
}

/**
 * User module state
 * @typedef {Object} UserState
 * @property {Object|null} user - firebase user object
 * @property {Array|null} invitations - User invitations
 * @property {Object} planInvitations - Plan invitations by plan ID
 * @property {Array|null} userPlanInvites - User plan invites
 * @property {Error|null} signInError - Sign-in error if any
 * @property {boolean} isAppLoading - Application loading state
 * @property {boolean} isMFAEnabled - Whether MFA is enabled for the user
 * @property {boolean} isInitialized - Whether auth service is initialized
 * @property {number|null} tokenRefreshInterval - Token refresh interval ID
 * @property {Map|null} featureAccessCache - Cache for feature access checks
 * @property {Function|null} boundRefreshToken - Bound function for token refresh on network change
 */
const state = {
  user: null,
  invitations: null,
  planInvitations: {},
  userPlanInvites: null,
  signInError: null,
  isAppLoading: false,
  isMFAEnabled: false,
  isInitialized: false,
  tokenRefreshInterval: null,
  featureAccessCache: null,
  boundRefreshToken: null
}

/**
 * Race a promise against a timeout
 * @param {Promise} promise - The promise to race
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} timeoutMessage - Message for timeout error
 * @param {Function} [cleanup] - Optional cleanup function to call when promise wins the race
 * @returns {Promise} Result of the race
 */
const raceWithTimeout = (promise, timeoutMs, timeoutMessage, cleanup = null) => {
  let timeoutId

  const timeoutPromise = new Promise((resolve) => {
    timeoutId = setTimeout(() => {
      logger.warn(timeoutMessage)
      resolve(null)
    }, timeoutMs)
  })

  return Promise.race([
    promise.then((result) => {
      if (timeoutId) clearTimeout(timeoutId)
      if (cleanup) cleanup()
      return result
    }),
    timeoutPromise
  ])
}

/**
 * Standardized error handler
 * @param {Error} error - The error to handle
 * @param {string} context - Context where the error occurred
 * @param {boolean} [reportToSentry=true] - Whether to report to Sentry
 */
const handleError = (error, context, reportToSentry = true) => {
  logger.error('Error in context:', context, error.message)
  if (reportToSentry && Sentry) {
    Sentry.captureException(error)
  }
}

/**
 * Refreshes the user's token with timeout protection
 * @param {firebase.User} user - Firebase user object
 * @param {boolean} [force=true] - Whether to force refresh
 * @returns {Promise<string>} Token
 */
const refreshUserToken = async (user, force = true) => {
  if (!user) throw new Error('No authenticated user')

  return raceWithTimeout(user.getIdToken(force), 10000, 'Token refresh timeout')
}

const mutations = {
  setIsAppLoading(state, isLoading) {
    state.isAppLoading = isLoading
  },

  /**
   * Set initialization state
   * @param {boolean} isInitialized - Whether auth service is initialized
   */
  setInitialized(state, isInitialized) {
    state.isInitialized = isInitialized
  },

  /**
   * Set token refresh interval
   * @param {number|null} interval - Interval ID or null
   */
  setTokenRefreshInterval(state, interval) {
    state.tokenRefreshInterval = interval
  },

  /**
   * Set or clear the user
   * This is the central place for user updates to ensure consistency
   * @param {firebase.User|null} firebaseUser - Firebase user object or null
   */
  setUser(state, firebaseUser) {
    state.user = firebaseUser
  },

  /**
   * Set MFA enabled state
   * @param {UserState} state - Module state
   * @param {boolean} isEnabled - Whether MFA is enabled
   */
  setMFAEnabled(state, isEnabled) {
    state.isMFAEnabled = Boolean(isEnabled)
  },

  /**
   * Set pending invitations
   * @param {UserState} state - Module state
   * @param {Array} invitations - Pending invitations
   */
  setInvitations(state, invitations) {
    state.invitations = invitations
  },

  /**
   * Set user plan invites
   * @param {UserState} state - Module state
   * @param {Array} invitations - User plan invites
   */
  setUserPlanInvites(state, invitations) {
    state.userPlanInvites = invitations
  },

  /**
   * Set plan invitations
   * @param {UserState} state - Module state
   * @param {Object} invitations - Plan invitations
   */
  setPlanInvitations(state, invitations) {
    state.planInvitations = invitations
  },

  /**
   * Update plan invitations
   * @param {UserState} state - Module state
   * @param {Object} payload - Update payload
   * @param {string} payload.planId - Plan ID
   * @param {Object} payload.invitation - Invitation to add
   */
  updatePlanInvitations(state, { planId, invitation }) {
    if (has(state.planInvitations, planId)) {
      state.planInvitations[planId].push(invitation)
    } else {
      state.planInvitations[planId] = [invitation]
    }
  },

  /**
   * Remove plan invite
   * @param {UserState} state - Module state
   * @param {Object} payload - Remove payload
   * @param {string} payload.planId - Plan ID
   * @param {string} payload.recipient - Recipient email
   */
  removePlanInvite(state, { planId, recipient }) {
    if (!state.planInvitations[planId]) return

    state.planInvitations[planId] = state.planInvitations[planId].filter(
      (plan) => plan.recipient !== recipient
    )
  },

  /**
   * Set sign-in error
   * @param {UserState} state - Module state
   * @param {Error|null} error - Sign-in error
   */
  setSignInError(state, error) {
    state.signInError = error
  },

  /**
   * Set feature access cache
   * @param {UserState} state - Module state
   * @param {Map|null} cache - Feature access cache
   */
  setFeatureAccessCache(state, cache) {
    state.featureAccessCache = cache
  },

  /**
   * Set bound refresh token function
   * @param {UserState} state - Module state
   * @param {Function|null} boundFn - Bound refresh token function
   */
  setBoundRefreshToken(state, boundFn) {
    state.boundRefreshToken = boundFn
  }
}

/**
 * Getters for user module
 */
const getters = {
  /**
   * Returns true if app is loading
   * @param {UserState} state - Module state
   * @returns {boolean} Whether app is loading
   */
  isAppLoading: (state) => state.isAppLoading,

  /**
   * Returns true if the current user is admin for a board
   * @param {UserState} state - Module state
   * @param {Object} getters - Module getters
   * @param {Object} rootState - Root state
   * @returns {Function} Function to check admin status
   */
  isAdmin: (state, getters, rootState) => (boardId) => {
    if (!boardId || !state.user) return false

    const targetBoard = find(rootState.board.boards, (board) => board.boardId === boardId)
    if (!targetBoard || !targetBoard.accessLevels) return false

    return some(
      targetBoard.accessLevels,
      (access) => access.uid === state.user.uid && access.accessLevel === 'admin'
    )
  },

  /**
   * Returns true if the current user is the plan creator
   * @param {UserState} state - Module state
   * @returns {Function} Function to check plan creator status
   */
  isPlanCreator: (state, getters, rootState) => (boardId) => {
    if (!boardId || !state.user) return false

    const targetBoard = find(rootState.board.boards, (board) => board.boardId === boardId)
    if (!targetBoard) return false

    return (
      (targetBoard.owners?.length === 1 && targetBoard.owners[0] === state.user.uid) ||
      targetBoard.createdBy === state.user.uid
    )
  },

  /**
   * Returns true if the current user is admin or plan creator
   * @param {UserState} state - Module state
   * @param {Object} getters - Module getters
   * @returns {Function} Function to check admin or plan creator status
   */
  isAdminOrPlanCreator: (state, getters) => (boardId) =>
    getters.isAdmin(boardId) || getters.isPlanCreator(boardId),

  /**
   * Returns true if the current user is executive
   * @param {UserState} state - Module state
   * @returns {Function} Function to check executive status
   */
  isExecutive: (state, getters, rootState) => (boardId) => {
    if (!boardId || !state.user) return false

    const targetBoard = find(rootState.board.boards, (board) => board.boardId === boardId)
    if (!targetBoard || !targetBoard.accessLevels) return false

    return some(
      targetBoard.accessLevels,
      (access) => access.uid === state.user.uid && access.accessLevel === 'executive'
    )
  },

  /**
   * Returns true if the current user is finance
   * @param {UserState} state - Module state
   * @returns {Function} Function to check finance status
   */
  isFinance: (state, getters, rootState) => (boardId) => {
    if (!boardId || !state.user) return false

    const targetBoard = find(rootState.board.boards, (board) => board.boardId === boardId)
    if (!targetBoard || !targetBoard.accessLevels) return false

    return some(
      targetBoard.accessLevels,
      (access) => access.uid === state.user.uid && access.accessLevel === 'finance'
    )
  },

  /**
   * Returns true if the current user is HR
   * @param {UserState} state - Module state
   * @returns {Function} Function to check HR status
   */
  isHR: (state, getters, rootState) => (boardId) => {
    if (!boardId || !state.user) return false

    const targetBoard = find(rootState.board.boards, (board) => board.boardId === boardId)
    if (!targetBoard || !targetBoard.accessLevels) return false

    return some(
      targetBoard.accessLevels,
      (access) => access.uid === state.user.uid && access.accessLevel === 'hr'
    )
  },

  /**
   * Returns true if the current user is manager
   * @param {UserState} state - Module state
   * @returns {Function} Function to check manager status
   */
  isManager: (state, getters, rootState) => (boardId) => {
    if (!boardId || !state.user) return false

    const targetBoard = find(rootState.board.boards, (board) => board.boardId === boardId)
    if (!targetBoard || !targetBoard.accessLevels) return false

    return some(
      targetBoard.accessLevels,
      (access) => access.uid === state.user.uid && access.accessLevel === 'manager'
    )
  },

  /**
   * Returns true if the current user is in the given access group
   * @param {UserState} state - Module state
   * @returns {Function} Function to check access group membership
   */
  isInAccessGroup:
    (state, _, rootState) =>
    ({ boardId, accessGroup }) => {
      if (!boardId || !state.user || !accessGroup) return false

      const targetBoard = find(rootState.board.boards, (board) => board.boardId === boardId)
      if (!targetBoard || !targetBoard.accessLevels) return false

      const access = targetBoard.accessLevels.find((access) => access.uid === state.user.uid)
      return access ? accessGroup.includes(access.accessLevel) : false
    },

  /**
   * Returns the user's role for a board
   * @param {UserState} state - Module state
   * @param {Object} getters - Module getters
   * @param {Object} rootState - Root state
   * @returns {Function} Function to get role
   */
  role: (state, getters, rootState) => (boardId) => {
    if (!boardId || !state.user) return null

    const targetBoard = find(rootState.board.boards, (board) => board.boardId === boardId)
    if (!targetBoard || !targetBoard.accessLevels) return null

    return find(targetBoard.accessLevels, (access) => access.uid === state.user.uid)?.accessLevel
  },

  /**
   * Returns the current user
   * @param {UserState} state - Module state
   * @returns {Object|null} Current user
   */
  user: (state) => state.user,

  /**
   * Returns the current user's UID
   * @param {UserState} state - Module state
   * @returns {string|null} Current user's UID
   */
  uid: (state) => state.user?.uid || null,

  /**
   * Returns the current user's email
   * @param {UserState} state - Module state
   * @returns {string|null} Current user's email
   */
  myEmail: (state) => state.user?.email || null,

  /**
   * Returns the current user's photo URL
   * @param {UserState} state - Module state
   * @returns {string} Current user's photo URL
   */
  userPic: (state) => state.user?.photoURL || '',

  /**
   * Returns the current user's display name
   * @param {UserState} state - Module state
   * @returns {string} Current user's display name
   */
  userDisplayName: (state) => state.user?.displayName || '',

  /**
   * Returns true if the user is logged in
   * @param {UserState} state - Module state
   * @returns {boolean} Whether user is logged in
   */
  isLoggedIn: (state) => state.user !== null,

  /**
   * Returns true if the user has pending invitations
   * @param {UserState} state - Module state
   * @returns {boolean} Whether user has pending invitations
   */
  hasPendingInvitations: (state) => (state.invitations?.length || 0) > 0,

  /**
   * Returns true if the user has plan invites
   * @param {UserState} state - Module state
   * @returns {boolean} Whether user has plan invites
   */
  hasUserPlanInvites: (state) => (state.userPlanInvites?.length || 0) > 0,

  /**
   * Returns the user's pending plan invites (snapshot fetched at login)
   * @param {UserState} state - Module state
   * @returns {Array} User plan invites
   */
  userPlanInvites: (state) => state.userPlanInvites || [],

  /**
   * Returns pending plan invitations by plan ID
   * @param {UserState} state - Module state
   * @returns {Function} Function to get pending plan invitations
   */
  pendingPlanInvitationsByPlanId: (state) => (planId) => state.planInvitations?.[planId] || [],

  /**
   * Returns sign-in error
   * @param {UserState} state - Module state
   * @returns {Error|null} Sign-in error
   */
  signInError: (state) => state.signInError,

  /**
   * Returns true if MFA is enabled for the current user
   * @param {UserState} state - Module state
   * @returns {boolean} Whether MFA is enabled
   */
  isMFAEnabled: (state) => state.isMFAEnabled,

  /**
   * Returns true if auth service is initialized
   */
  isInitialized: (state) => state.isInitialized,

  /**
   * Returns true if user email is verified
   * @param {UserState} state - Module state
   * @returns {boolean} Whether user email is verified
   */
  isVerified: (state) => state.user?.emailVerified || false,

  /**
   * Returns feature access cache
   */
  featureAccessCache: (state) => state.featureAccessCache,

  /**
   * Returns true if the user has access to a board
   * @param {UserState} state - Module state
   * @returns {Function} Function to check board access
   */
  hasAccessToBoard:
    (state, _, rootState) =>
    ({ boardId }) => {
      if (!boardId || !state.user) return false

      const targetBoard = find(rootState.board.boards, (board) => board.boardId === boardId)
      if (!targetBoard || !targetBoard.accessLevels) return false

      return !!targetBoard.accessLevels.find((access) => access.uid === state.user.uid)
    }
}

/**
 * Actions for user module
 */
const actions = {
  /**
   * Initialize the auth service and set up listeners
   * Called once at application startup
   */
  async initialize({ getters, commit, dispatch }) {
    // Skip if already initialized
    if (getters.isInitialized) return

    // Set initialization in progress
    const initPromise = new Promise((resolve) => {
      try {
        // Register the MFA event listener
        authBus.on(AuthActions.MFAVerified, async () => {
          try {
            const currentUser = firebase.auth().currentUser
            if (currentUser) {
              await refreshUserToken(currentUser, true)
              await dispatch('syncUserState', currentUser)
            }
          } catch (error) {
            logger.error('Error in MFA verification handler:', error.message)
            if (Sentry) Sentry.captureException(error)
          }
        })

        // Set up auth state observer with error handling
        firebase.auth().onAuthStateChanged(
          async (firebaseUser) => {
            try {
              await dispatch('syncUserState', firebaseUser)
            } catch (error) {
              logger.error('Error in auth state change handler:', error.message)
              if (Sentry) Sentry.captureException(error)
            } finally {
              commit('setInitialized', true)
              resolve()
            }
          },
          (error) => {
            // Handle Firebase auth error
            logger.error('Firebase auth observer error:', error.message)
            if (Sentry) Sentry.captureException(error)
            commit('setInitialized', true)
            resolve()
          }
        )
      } catch (error) {
        logger.error('Error during auth initialization:', error.message)
        if (Sentry) Sentry.captureException(error)
        commit('setInitialized', true)
        resolve()
      }
    })

    // Wait for either auth to initialize or timeout
    await raceWithTimeout(
      initPromise,
      10000,
      'Auth initialization timed out, proceeding with best effort',
      () => commit('setInitialized', true)
    )
  },

  /**
   * Synchronize the user state with Firebase
   * This is the central action for handling user state changes
   * @param {firebase.User|null} firebaseUser - Firebase user object
   */
  async syncUserState({ commit, dispatch }, firebaseUser) {
    // If no user, clean up auth state
    if (!firebaseUser) {
      commit('setUser', null)
      commit('setMFAEnabled', false)
      dispatch('stopTokenRefresh')
      commit('setIsAppLoading', false)
      return
    }

    try {
      // Check MFA status
      const idTokenResult = await firebaseUser.getIdTokenResult(true)
      await setAPIUrl()
      const mfaEnabled = Boolean(idTokenResult.claims.mfaEnabled)
      const mfaVerified = Boolean(idTokenResult.claims.mfaVerified)

      // If MFA is required but not verified, don't set as authenticated
      if (mfaEnabled && !mfaVerified) {
        logger.info('User needs MFA verification')
        commit('setIsAppLoading', false)
        return
      }

      // Update user state and start refresh interval
      commit('setUser', firebaseUser)
      commit('setMFAEnabled', mfaEnabled)

      // Setup user-related services
      dispatch('setupTracking', firebaseUser)

      // Start token refresh interval
      dispatch('startTokenRefresh')

      // Load user data
      await dispatch('loadUserData', firebaseUser)
    } catch (error) {
      logger.error('Error syncing user state:', error.message)
      if (Sentry) Sentry.captureException(error)
      commit('setSignInError', error)
    } finally {
      commit('setIsAppLoading', false)
    }
  },

  /**
   * Sync the current Firebase user to the Vuex store
   * Use this before security-critical operations
   * @returns {Promise<firebase.User|null>} The current user or null
   */
  async syncCurrentUser({ commit }) {
    try {
      const currentUser = firebase.auth().currentUser
      commit('setUser', currentUser)
      return currentUser
    } catch (error) {
      logger.error('Error syncing current user:', error.message)
      if (Sentry) Sentry.captureException(error)
      return null
    }
  },

  /**
   * Sets up user tracking for analytics
   * @param {firebase.User} userObj - Firebase user object
   */
  async setupTracking(context, userObj) {
    const { isEmbedded } = getEmbedStatus()
    const namespace = isEmbedded ? await getDayforceNamespace(userObj) : ''

    if (isEmbedded && PYLON_ALLOWED_NAMESPACES.includes(namespace)) {
      try {
        const headers = await getAuthHeader()
        const res = await axios.post(`${AUTH_URL}/users/pylon-hash`, {}, { headers })
        const email = res.data.email || userObj.email
        window.pylon = {
          chat_settings: {
            app_id: 'c7852731-fbef-44b1-8bf6-17296c968c1a',
            email,
            name: userObj.displayName || email,
            email_hash: res.data.hash
          }
        }
      } catch (err) {
        logger.error('Error getting pylon hash:', err.message)
      }

      loadPylonScript()
    }

    // Setup pendo tracking when in Dayforce
    if (
      globalThis.location.hostname.includes('dayforcehcm.com') ||
      globalThis.location.hostname.includes('dayforce.com')
    ) {
      Sentry.getGlobalScope().setUser({
        id: userObj.uid,
        email: userObj.email,
        username: userObj.displayName
      })

      // eslint-disable-next-line no-undef -- pendo injected by external script tag
      if (!pendo?.initialize) {
        Sentry.captureMessage('Pendo is not initialized', 'error')
        return
      }
      try {
        const token_result = await userObj.getIdTokenResult()

        if (!token_result.claims['dfid']) {
          Sentry.captureMessage('Claim does not have DFID. Failed to setup Pendo', 'error')
          return
        }

        // eslint-disable-next-line no-undef -- pendo injected by external script tag
        pendo.initialize({
          jwt: token_result.claims['dfid'],
          signingKeyName: 'ceri-ceri-pV0ME'
        })
      } catch (err) {
        Sentry.captureMessage(
          'Failed to get the auth headers. Failed to setup pendo monitoring',
          'error'
        )
        Sentry.captureException(err)
      }

      return
    }

    if (!globalThis.location.hostname.endsWith('.agentnoon.com')) return

    if (!userObj || !userObj.uid) return

    Sentry.getGlobalScope().setUser({
      id: userObj.uid,
      email: userObj.email,
      username: userObj.displayName
    })

    window.faro?.api.setUser({
      id: userObj.uid,
      name: userObj.displayName || userObj.email
    })

    const company = userObj.email ? userObj.email.split('@')[1] : ''

    if (window.pendo?.initialize) {
      try {
        window.pendo.initialize({
          visitor: {
            id: userObj.uid,
            ...(userObj.email ? { email: userObj.email } : {}),
            ...(userObj.displayName ? { full_name: userObj.displayName } : {})
          },
          account: {
            id: company || 'unknown'
          }
        })
      } catch (err) {
        Sentry.captureMessage('Failed to initialize Pendo for agentnoon', 'error')
        Sentry.captureException(err)
      }
    } else {
      Sentry.captureMessage('Pendo is not initialized', 'error')
    }

    if (globalThis.mixpanel) {
      window.mixpanel.identify(userObj.uid)
      window.mixpanel.people.set({
        $email: userObj.email,
        creationTime: userObj.metadata?.creationTime,
        lastSignInTime: userObj.metadata?.lastSignInTime
      })
      window.mixpanel.track('login')

      if (company) {
        window.mixpanel.set_group('company', company)
      }
    }

    if (window.ga4) {
      window.ga4.setUserProperties({
        company,
        email: userObj.email,
        id: userObj.uid
      })
    }

    if (window.smartlook) {
      window.smartlook('identify', userObj.uid, {
        name: userObj.displayName || '',
        email: userObj.email
      })
    }
  },

  /**
   * Starts regular token refresh to ensure tokens stay valid
   * Handles network issues and retries gracefully
   * @param {Function} context.commit - Commit function
   * @param {Function} context.dispatch - Dispatch function
   */
  startTokenRefresh({ state, commit, dispatch }) {
    // Clear existing interval if any
    if (state.tokenRefreshInterval) {
      clearInterval(state.tokenRefreshInterval)
    }

    let consecutiveFailures = 0

    const tokenRefreshInterval = setInterval(
      async () => {
        try {
          // Skip refresh if offline
          if (!navigator.onLine) {
            logger.info('Device offline, skipping token refresh')
            return
          }

          const currentUser = firebase.auth().currentUser
          if (currentUser) {
            await raceWithTimeout(currentUser.getIdToken(true), 15000, 'Token refresh timeout')

            // Reset failure count on success
            if (consecutiveFailures > 0) {
              logger.info(`Token refresh succeeded after ${consecutiveFailures} failures`)
              consecutiveFailures = 0
            }
          }
        } catch (error) {
          consecutiveFailures++
          handleError(error, 'Token refresh')

          // Escalating recovery measures
          if (consecutiveFailures >= 3) {
            logger.warn('Multiple token refresh failures, forcing reauth')

            // After 5 failures, reload the page as last resort
            if (consecutiveFailures >= 5) {
              logger.error('Critical token refresh failure, forcing reload')
              window.location.reload()
            }
          }
        }
      },
      10 * 60 * 1000 // 10 minutes
    )

    commit('setTokenRefreshInterval', tokenRefreshInterval)

    // Add network change listener
    const boundRefreshToken = () => dispatch('refreshTokenOnNetworkChange')
    commit('setBoundRefreshToken', boundRefreshToken)

    window.addEventListener('online', boundRefreshToken)
  },

  /**
   * Refresh token when network connectivity changes
   * @returns {Promise<void>}
   */
  async refreshTokenOnNetworkChange() {
    logger.info('Network connectivity restored, refreshing token')
    try {
      const currentUser = firebase.auth().currentUser
      if (currentUser) {
        await raceWithTimeout(
          currentUser.getIdToken(true),
          10000,
          'Token refresh timeout after network change'
        )
      }
    } catch (error) {
      logger.error('Error refreshing token after network change:', error.message)
      if (Sentry) Sentry.captureException(error)
    }
  },

  /**
   * Stops token refresh interval and removes listeners
   * @param {Function} context.commit - Commit function
   */
  stopTokenRefresh({ state, commit }) {
    if (state.tokenRefreshInterval) {
      clearInterval(state.tokenRefreshInterval)
      commit('setTokenRefreshInterval', null)
    }

    // Remove network listener
    if (state.boundRefreshToken) {
      window.removeEventListener('online', state.boundRefreshToken)
      commit('setBoundRefreshToken', null)
    }
  },

  /**
   * Loads user data from various sources
   * Handles offline scenarios and API failures gracefully
   * @param {firebase.User} userObj - Firebase user object
   * @returns {Promise<Object>} Load result
   */
  async loadUserData({ commit, dispatch, state }, userObj) {
    try {
      // Check if we're online
      if (!navigator.onLine) {
        logger.warn('Device appears to be offline, deferring data loading')
        dispatch('setupOfflineRecovery', userObj)
        return { hasInvites: false, hasPlanInvites: false, offline: true }
      }

      // Fetch user data with timeout protection
      const fetchPromise = Promise.allSettled([
        dispatch('fetchAllBoards', userObj.uid),
        dispatch('fetchPendingInvitations', { email: userObj.email }),
        dispatch('fetchPendingPlanInvitations', { email: userObj.email }),
        dispatch('cardContent/fetchSettings', userObj.uid)
      ])

      // Use race with timeout pattern
      await raceWithTimeout(
        fetchPromise,
        30000,
        'API calls timed out, proceeding with partial data'
      )

      // Process invitations
      const hasInvites = state.invitations && state.invitations.length > 0
      const hasPlanInvites = state.userPlanInvites && state.userPlanInvites.length > 0

      if (hasPlanInvites) {
        try {
          // Using dynamic import to avoid circular dependencies.
          // The service runs the scenario claim itself before navigating.
          const acceptPlanInvitation = (
            await import('@/services/acceptPlanInvitationForNewUser.service')
          ).default
          await acceptPlanInvitation()
        } catch (acceptError) {
          logger.error('Error accepting plan invitation:', acceptError.message)
          if (Sentry) Sentry.captureException(acceptError)
        }
      } else {
        // Self-heal scenario access from user-group shares (fire-and-forget,
        // idempotent; backend no-ops unless the user is an instance member).
        dispatch('claimUserGroupScenarios')
        if (hasInvites) {
          if (window.mixpanel) window.mixpanel.track('new_invited_user')
        }
      }

      // Fetch recent invites non-blocking
      try {
        await raceWithTimeout(
          dispatch('fetchRecentInvites'),
          5000,
          'Recent invites fetch timed out'
        )
      } catch (invitesError) {
        logger.warn('Error fetching recent invites:', invitesError.message)
      }

      return { hasInvites, hasPlanInvites, offline: false }
    } catch (error) {
      logger.error('Error in user data loading:', error.message)
      if (Sentry) Sentry.captureException(error)
      commit('setSignInError', error)

      return { hasInvites: false, hasPlanInvites: false, error }
    }
  },

  /**
   * Set up recovery for when the device comes back online
   * @param {firebase.User} userObj - Firebase user object
   */
  setupOfflineRecovery({ dispatch }, userObj) {
    const handleOnline = async () => {
      logger.info('Device back online, loading user data')
      window.removeEventListener('online', handleOnline)

      try {
        await dispatch('loadUserData', userObj)
      } catch (error) {
        logger.error('Error in offline recovery:', error.message)
        if (Sentry) Sentry.captureException(error)
      }
    }

    window.addEventListener('online', handleOnline)
  },

  /**
   * Sign in with Google
   * @param {Function} context.commit - Commit function
   * @param {boolean} isSwitching - Whether user is switching authentication methods
   * @returns {Promise<boolean>} Success status
   */
  async googleSignIn({ dispatch, commit }, isSwitching = false) {
    commit('setSignInError', null)

    try {
      const provider = new firebase.auth.GoogleAuthProvider()
      provider.setCustomParameters({
        prompt: 'select_account'
      })
      firebase.auth().useDeviceLanguage()

      let result
      let restoreVerifiedStatus = false

      if (isSwitching) {
        // Link Google to existing account
        const user = firebase.auth().currentUser
        if (!user) {
          throw new Error('No authenticated user found. Please sign in first.')
        }

        const idTokenResult = await user.getIdTokenResult(true)
        const mfaEnabled = Boolean(idTokenResult.claims.mfaEnabled)
        const mfaVerified = Boolean(idTokenResult.claims.mfaVerified)

        // Check if MFA needs to be restored
        if (mfaEnabled && !mfaVerified) {
          restoreVerifiedStatus = true
        }

        result = await user.linkWithPopup(provider)

        if (restoreVerifiedStatus) {
          await restoreMfaVerifiedClaim()
        }
      } else {
        // Regular sign-in flow
        result = await firebase.auth().signInWithPopup(provider)
      }

      // Sync user state
      await dispatch('syncUserState', result.user)

      // Force token refresh
      await result.user.getIdToken(true)

      // Track signup if new user
      if (!isSwitching && result.additionalUserInfo.isNewUser) {
        if (window.mixpanel) window.mixpanel.track('signup', { provider: 'google' })
      }

      return true
    } catch (error) {
      logger.error('Google sign-in error:', error.message)
      if (Sentry) Sentry.captureException(error)
      commit('setSignInError', error)
      return false
    }
  },

  /**
   * Sign in with Microsoft
   * @param {boolean} isSwitching - Whether user is switching authentication methods
   * @returns {Promise<boolean>} Success status
   */
  async microsoftSignIn({ dispatch, commit }, isSwitching = false) {
    commit('setSignInError', null)

    try {
      const provider = new firebase.auth.OAuthProvider('microsoft.com')
      firebase.auth().useDeviceLanguage()

      let result
      let restoreVerifiedStatus = false

      if (isSwitching) {
        // Link Microsoft to existing account
        const user = firebase.auth().currentUser
        if (!user) {
          throw new Error('No authenticated user found. Please sign in first.')
        }

        const idTokenResult = await user.getIdTokenResult(true)
        const mfaEnabled = Boolean(idTokenResult.claims.mfaEnabled)
        const mfaVerified = Boolean(idTokenResult.claims.mfaVerified)

        // Check if MFA needs to be restored
        if (mfaEnabled && !mfaVerified) {
          restoreVerifiedStatus = true
        }

        result = await user.linkWithPopup(provider)

        if (restoreVerifiedStatus) {
          await restoreMfaVerifiedClaim()
        }
      } else {
        // Regular sign-in flow
        result = await firebase.auth().signInWithPopup(provider)
      }

      // Sync user state
      await dispatch('syncUserState', result.user)

      // Force token refresh
      await result.user.getIdToken(true)

      // Track signup if new user
      if (!isSwitching && result.additionalUserInfo.isNewUser) {
        if (window.mixpanel) window.mixpanel.track('signup', { provider: 'microsoft' })
      }

      return true
    } catch (error) {
      logger.error('Microsoft sign-in error:', error.message)
      if (Sentry) Sentry.captureException(error)
      commit('setSignInError', error)
      return false
    }
  },

  /**
   * Sign in with Okta
   * @param {Object} options - Sign-in options
   * @param {string} options.providerId - Provider ID
   * @param {boolean} [options.isSwitching=false] - Whether user is switching authentication methods
   * @returns {Promise<boolean>} Success status
   */
  async oktaSignIn({ dispatch, commit }, { providerId, isSwitching = false }) {
    commit('setSignInError', null)

    try {
      const provider = new firebase.auth.OAuthProvider(providerId)
      firebase.auth().useDeviceLanguage()

      let result
      let restoreVerifiedStatus = false

      if (isSwitching) {
        // Link Okta to existing account
        const user = firebase.auth().currentUser
        if (!user) {
          throw new Error('No authenticated user found. Please sign in first.')
        }

        const idTokenResult = await user.getIdTokenResult(true)
        const mfaEnabled = Boolean(idTokenResult.claims.mfaEnabled)
        const mfaVerified = Boolean(idTokenResult.claims.mfaVerified)

        // Check if MFA needs to be restored
        if (mfaEnabled && !mfaVerified) {
          restoreVerifiedStatus = true
        }

        result = await user.linkWithPopup(provider)

        if (restoreVerifiedStatus) {
          await restoreMfaVerifiedClaim()
        }
      } else {
        // Regular sign-in flow
        result = await firebase.auth().signInWithPopup(provider)
      }

      // Sync user state
      await dispatch('syncUserState', result.user)

      // Force token refresh
      await result.user.getIdToken(true)

      // Track signup if new user
      if (!isSwitching && result.additionalUserInfo.isNewUser) {
        if (window.mixpanel) window.mixpanel.track('signup', { provider: 'okta' })
      }

      return true
    } catch (error) {
      logger.error('Okta sign-in error:', error.message)
      if (Sentry) Sentry.captureException(error)
      commit('setSignInError', error)
      return false
    }
  },

  /**
   * Sign in with Firebase SAML
   * @param {Object} options - Sign-in options
   * @param {string} options.providerId - Provider ID
   * @returns {Promise<boolean>} Success status
   */
  async firebaseSamlSignIn({ commit, dispatch }, { providerId }) {
    commit('setSignInError', null)

    try {
      const provider = new firebase.auth.SAMLAuthProvider(providerId)
      firebase.auth().useDeviceLanguage()

      const result = await firebase.auth().signInWithPopup(provider)

      // Sync user state
      await dispatch('syncUserState', result.user)

      // Force token refresh
      await result.user.getIdToken(true)

      // Track signup if new user
      if (result.additionalUserInfo.isNewUser) {
        if (window.mixpanel) window.mixpanel.track('signup', { provider: 'saml' })
      }

      return true
    } catch (error) {
      logger.error('SAML sign-in error:', error.message)
      if (Sentry) Sentry.captureException(error)
      commit('setSignInError', error)
      return false
    }
  },

  /**
   * Sign in with SAML
   * @param {Object} options - Sign-in options
   * @param {string} options.providerId - Provider ID
   * @returns {Promise<boolean>} Success status
   */
  async samlSignIn({ commit }, { providerId }) {
    commit('setSignInError', null)

    try {
      const remoteConfig = getRemoteConfig(firebase.app())
      await fetchAndActivate(remoteConfig)

      const identityProviderConfig = JSON.parse(
        getValue(remoteConfig, providerId.replace('saml.', '')).asString()
      )

      if (identityProviderConfig.sso_login_url) {
        window.location.href = identityProviderConfig.sso_login_url
        return true
      }

      throw new Error('SAML provider not found')
    } catch (error) {
      logger.error('SAML sign-in error:', error.message)
      if (Sentry) Sentry.captureException(error)
      commit('setSignInError', error)
      return false
    }
  },

  /**
   * Sign in with custom token
   * @param {Function} context.commit - Commit function
   * @param {string} token - Custom token
   * @returns {Promise<boolean>} Success status
   */
  async customTokenSignIn({ dispatch, commit }, token) {
    commit('setSignInError', null)

    try {
      // Ensure we don't route using stale board state from a previous session.
      await dispatch('reset')

      firebase.auth().useDeviceLanguage()
      const result = await firebase.auth().signInWithCustomToken(token)

      // Sync user state
      await dispatch('syncUserState', result.user)

      // Force token refresh
      await result.user.getIdToken(true)

      // Track signup if new user
      if (result.additionalUserInfo.isNewUser) {
        if (window.mixpanel) window.mixpanel.track('signup', { provider: 'custom_token' })
      }

      return true
    } catch (error) {
      logger.error('Custom token sign-in error:', error.message)
      if (Sentry) Sentry.captureException(error)
      commit('setSignInError', error)
      return false
    }
  },

  /**
   * Log out the current user
   * @returns {Promise<boolean>} Success status
   */
  async logout({ commit, dispatch }) {
    try {
      // Clean up token refresh interval
      await dispatch('stopTokenRefresh')

      // Call backend logout API
      await logout()

      // Sign out from Firebase
      await firebase.auth().signOut()

      // Clean up local state
      commit('setUser', null)
      await dispatch('reset')
      localStorage.removeItem('always-on-fields')

      // Emit logout event to clear caches (e.g., feature access service)
      authBus.emit(AuthActions.Logout)

      return true
    } catch (error) {
      logger.error('Logout error:', error.message)
      if (Sentry) Sentry.captureException(error)
      return false
    } finally {
      if (window.Intercom) window.Intercom('shutdown')
    }
  },

  /**
   * Update user profile
   * @param {Object} profile - Profile updates
   * @param {string} profile.displayName - Display name
   * @returns {Promise<boolean>} Success status
   */
  async updateProfile({ commit }, { displayName }) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) throw new Error('No authenticated user')

      await currentUser.updateProfile({ displayName })

      // Update state with new Firebase user
      commit('setUser', currentUser)

      return true
    } catch (error) {
      handleError(error, 'updateProfile')
      return false
    }
  },

  /**
   * Get the current authentication state of the user
   * @returns {Promise<string>} Auth state
   */
  async getCurrentAuthState({ dispatch, commit, getters }) {
    try {
      // First sync the current Firebase user to state
      await dispatch('syncCurrentUser')

      const user = firebase.auth().currentUser
      if (!user) {
        return AUTH_STATE.UNAUTHENTICATED
      }

      try {
        // Refresh token to get latest claims
        await refreshUserToken(user)

        // Ensure auth state is updated on the backend (triggers MFA claim setting)
        let claimsWereUpdated = false
        try {
          const authStateResponse = await validateAuthState()
          if (authStateResponse?.claimsUpdated) {
            claimsWereUpdated = true
            logger.info('MFA claims were updated, refreshing token')
          }
        } catch (ensureStateError) {
          logger.error(
            'Failed to ensure auth state on backend, continuing:',
            ensureStateError.message
          )
        }

        const idTokenResult = await user.getIdTokenResult(claimsWereUpdated || true)
        const isDayforceEmbedUser =
          idTokenResult.claims?.source === 'dayforce' &&
          typeof window !== 'undefined' &&
          window.self !== window.top

        // Dayforce iframe users are treated as verified and do not require MFA/email verification
        if (isDayforceEmbedUser) {
          commit('setMFAEnabled', false)
          return AUTH_STATE.VERIFIED
        }

        // Check for email verification
        if (!user.emailVerified) {
          return AUTH_STATE.UNVERIFIED
        }

        // Check for MFA requirement
        const mfaEnabled = Boolean(idTokenResult.claims.mfaEnabled)

        // Update MFA state in store
        commit('setMFAEnabled', mfaEnabled)

        if (mfaEnabled) {
          const mfaVerified = Boolean(idTokenResult.claims.mfaVerified)
          if (!mfaVerified) {
            return AUTH_STATE.NEEDS_MFA
          }
        }

        return AUTH_STATE.VERIFIED
      } catch (error) {
        handleError(error, 'refreshing token for auth state check')

        // Fall back to store state on error
        return getters.isLoggedIn
          ? getters.isVerified
            ? AUTH_STATE.VERIFIED // Default to VERIFIED on error, better UX than blocking
            : AUTH_STATE.UNVERIFIED
          : AUTH_STATE.UNAUTHENTICATED
      }
    } catch (error) {
      handleError(error, 'getCurrentAuthState')

      // Default to unauthenticated on error for security
      return AUTH_STATE.UNAUTHENTICATED
    }
  },

  /**
   * Set application loading state
   * @param {boolean} status - Loading status
   */
  async changeIsAppLoading({ commit }, status) {
    commit('setIsAppLoading', status)
  },

  /**
   * Update user display name
   * @param {Object} options - Update options
   * @param {string} options.displayName - Display name
   */
  async updateName({ commit }, { displayName }) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) throw new Error('No authenticated user')

      await currentUser.updateProfile({ displayName })

      // Update state with new Firebase user
      commit('setUser', currentUser)
    } catch (error) {
      handleError(error, 'updateName')
    }
  },

  /**
   * Fetch pending invitations for email
   * @param {Object} options - Fetch options
   * @param {string} options.email - Email
   */
  async fetchPendingInvitations(context, { email }) {
    try {
      const normalizedEmail = typeof email === 'string' ? email.trim().toLowerCase() : ''

      if (!normalizedEmail) {
        context.commit('setInvitations', [])
        return []
      }

      const invitations = await getPendingInvitationsForEmail({ email: normalizedEmail })
      const pendingInvitations = invitations
        .filter((invite) => !invite.ignore)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      context.commit('setInvitations', pendingInvitations)
      return pendingInvitations
    } catch (error) {
      logger.error('Error fetching pending invitations:', error.message)
      if (Sentry) Sentry.captureException(error)
      return []
    }
  },

  /**
   * Fetch pending invitations for board
   * @param {Object} options - Fetch options
   * @param {string} options.boardId - Board ID
   * @returns {Promise<Array>} Invitations
   */
  async fetchPendingInvitationsForBoard(context, { boardId }) {
    try {
      return await getPendingInvitationsForBoard({ boardId })
    } catch (error) {
      logger.error('Error fetching pending invitations for board:', error.message)
      if (Sentry) Sentry.captureException(error)
      return []
    }
  },

  /**
   * Accept invitation
   * @param {Object} context - Action context
   * @param {Object} options - Accept options
   * @param {string} options.boardId - Board ID
   * @param {string} options.invitationId - Invitation ID
   * @throws {Error} Re-throws error if invitation acceptance fails
   */
  async acceptInvitation(context, { boardId, invitationId }) {
    try {
      await acceptInvitation({ boardId, id: invitationId })
      // Now an instance member: claim any user-group / plan scenario access, then
      // refresh boards so newly granted scenarios appear (claim refetches boards).
      await context.dispatch('claimUserGroupScenarios')
    } catch (error) {
      logger.error('Error accepting invitation:', error.message)
      if (Sentry) Sentry.captureException(error)
      throw error // Re-throw so caller can handle the error
    }
  },

  /**
   * Fire-and-forget: grant scenario access from user-group shares and pending
   * plan invitations for the signed-in user (backend gates on instance
   * membership), then refresh boards so any newly granted scenarios appear.
   * Idempotent and best-effort.
   * @param {Object} context - Action context
   */
  async claimUserGroupScenarios(context) {
    try {
      await claimUserGroupInvitations()
      await context.dispatch('fetchAllBoards', context.state.user.uid)
    } catch (error) {
      logger.error('Error claiming user group scenarios:', error.message)
      if (Sentry) Sentry.captureException(error)
    }
  },

  /**
   * Fetch person from main board
   * @param {Object} options - Fetch options
   * @param {string} options.boardId - Board ID
   * @returns {Promise<Object|null>} Person
   */
  async fetchPersonFromMainBoard(context, { boardId }) {
    try {
      const people = await getPeopleByEmail({ boardId, email: context.getters.myEmail })
      return people[0] || null
    } catch (error) {
      logger.error('Error fetching person from main board:', error.message)
      if (Sentry) Sentry.captureException(error)
      return null
    }
  },

  /**
   * Send plan invite to outsiders
   * @param {Object} invitation - Invitation data
   */
  async sendPlanInviteToOutsiders(
    context,
    {
      planName,
      planBoardId,
      boardId,
      invitedBy,
      inviterEmail,
      recipient,
      recipientName,
      accessLevel
    }
  ) {
    try {
      const invitation = {
        planName,
        planBoardId,
        boardId,
        invitedBy,
        inviterEmail,
        recipient,
        accessLevel,
        visited: false
      }

      context.commit('updatePlanInvitations', { planId: planBoardId, invitation })

      await sendPlanInviteToOutsiders({
        boardId,
        invitation,
        origin: window.location.origin,
        recipientName
      })
    } catch (error) {
      logger.error('Error sending plan invite to outsiders:', error.message)
      if (Sentry) Sentry.captureException(error)
    }
  },

  /**
   * Remove plan invite for outsiders
   * @param {Object} options - Remove options
   * @param {string} options.planBoardId - Plan board ID
   * @param {string} options.recipient - Recipient email
   */
  async removePlanInviteForOutsiders(context, { planBoardId, recipient }) {
    try {
      context.commit('removePlanInvite', { planId: planBoardId, recipient })
      await removePlanInvitationForOutsiders({ planBoardId, recipient })
    } catch (error) {
      logger.error('Error removing plan invite for outsiders:', error.message)
      if (Sentry) Sentry.captureException(error)
    }
  },

  /**
   * Fetch pending plan invitations
   * @param {Object} options - Fetch options
   * @param {string} options.email - Email
   * @returns {Promise<Array>} Invitations
   */
  async fetchPendingPlanInvitations(context, { email }) {
    try {
      const normalizedEmail = typeof email === 'string' ? email.trim().toLowerCase() : ''

      if (!normalizedEmail) {
        context.commit('setUserPlanInvites', [])
        return []
      }

      const invitations = await getPendingPlanInvitationsForEmail({ email: normalizedEmail })
      const myEmail =
        typeof context.getters.myEmail === 'string'
          ? context.getters.myEmail.trim().toLowerCase()
          : ''

      // Only set state if querying for the logged in user
      if (normalizedEmail === myEmail) {
        context.commit('setUserPlanInvites', invitations)
      }

      return invitations
    } catch (error) {
      logger.error('Error fetching pending plan invitations:', error.message)
      if (Sentry) Sentry.captureException(error)
      return []
    }
  },

  /**
   * Fetch pending plan invitations for board
   * @param {string} boardId - Board ID
   */
  async fetchPendingPlanInvitationsForBoard(context, boardId) {
    try {
      const planInvitations = await getPendingPlanInvitationsForBoard({ boardId })
      const invitationByPlanId = groupBy(planInvitations, 'planBoardId')
      context.commit('setPlanInvitations', invitationByPlanId)
    } catch (error) {
      logger.error('Error fetching pending plan invitations for board:', error.message)
      if (Sentry) Sentry.captureException(error)
    }
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
